import { useState, useRef, useEffect } from 'react'
import { Answer, PreviewJD } from '@/interfaces/answer'
import GameLayout from '@/layouts/GameLayout'
import { Send } from 'lucide-react'
import { SurveyQuestion, SurveyQuestionType } from '@/interfaces/survey'
import { User } from '@/interfaces/user'
import { ReviewJDDialog } from '@/components/ReviewJDDialog'
import { useConfirmJD } from '@/hooks/mutations/useConfirmJD'
import { useNavigate } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { UserSearchInput } from '@/components/UserSearchInput'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'

interface SurveyFormProps {
  title: string
  highlightedText: string
  questions: SurveyQuestion[]
  onSubmit: (
    payload: { surveyId: string; answers: Answer[]; targetUserId?: string },
    onSuccess: (answerId: string, previewJD: PreviewJD) => void
  ) => void
  personNameQuestionId?: string // The question ID that contains the person's name
  surveyId: string
  initialSelectedUser?: User // Pre-fill user for the search question
}

export const SurveyForm = ({
  title,
  highlightedText,
  questions,
  onSubmit,
  personNameQuestionId,
  surveyId,
  initialSelectedUser,
}: SurveyFormProps) => {
  // Initialize with pre-filled user if provided
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    if (initialSelectedUser && personNameQuestionId) {
      return { [personNameQuestionId]: initialSelectedUser.name }
    }
    return {}
  })
  const [selectedUsers, setSelectedUsers] = useState<Record<string, User>>(() => {
    if (initialSelectedUser && personNameQuestionId) {
      return { [personNameQuestionId]: initialSelectedUser }
    }
    return {}
  })
  // If initial user is provided and it's the first question, start at question 2
  const [currentVisibleQuestions, setCurrentVisibleQuestions] = useState(() => {
    if (initialSelectedUser && personNameQuestionId && questions.length > 0) {
      const firstQuestionId = questions[0]?.id
      // If the first question is the search question and it's pre-filled, start at 2
      if (firstQuestionId === personNameQuestionId) {
        return 2
      }
    }
    return 1
  })
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const [showReviewDialog, setShowReviewDialog] = useState(false)
  const [answerId, setAnswerId] = useState<string | null>(null)
  const [previewJD, setPreviewJD] = useState<PreviewJD | null>(null)
  const navigate = useNavigate()

  const { mutate: confirmJD, isPending: isConfirming } = useConfirmJD()
  const { user } = useAuth()

  // Get the person's name from the first question answer
  const personName = personNameQuestionId ? answers[personNameQuestionId] : null

  // Determine userRole: if selectedUsers is empty (CreateJD), use current user's role; otherwise use target user's role (SendJD)
  const userRole =
    Object.keys(selectedUsers).length === 0
      ? user?.role // CreateJD - use current user's role
      : Object.values(selectedUsers)[0]?.role // SendJD - use target user's role

  // Function to replace placeholders in text
  const replacePlaceholders = (text: string): string => {
    if (!personName) return text
    return text.replace(/\[tên người đó\]/g, personName)
  }

  // Function to process title with highlightedText and line breaks
  const renderTitle = () => {
    // Split by line breaks first
    const lines = title.split(/\\n|\n/)
    const result: React.ReactNode[] = []

    lines.forEach((line, lineIndex) => {
      // Check if this line contains the highlightedText
      const parts: React.ReactNode[] = []
      const highlightIndex = line.indexOf(highlightedText)

      if (highlightIndex !== -1) {
        // Add text before highlighted part
        if (highlightIndex > 0) {
          parts.push(line.substring(0, highlightIndex))
        }

        // Add highlighted text with primary color
        parts.push(
          <span key={`highlight-${lineIndex}`} className="text-primary">
            {highlightedText}
          </span>
        )

        // Add text after highlighted part
        const afterHighlight = highlightIndex + highlightedText.length
        if (afterHighlight < line.length) {
          parts.push(line.substring(afterHighlight))
        }
      } else {
        // No highlight in this line, just add the line
        parts.push(line)
      }

      // Add the processed line
      result.push(
        <span key={`line-${lineIndex}`}>
          {parts}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      )
    })

    return result
  }

  // Focus on the newly visible input
  useEffect(() => {
    const lastVisibleQuestion = questions[currentVisibleQuestions - 1]
    if (lastVisibleQuestion && inputRefs.current[lastVisibleQuestion.id]) {
      setTimeout(() => {
        const element = inputRefs.current[lastVisibleQuestion.id]
        element?.focus()
      }, 100)
    }
  }, [currentVisibleQuestions, questions])

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  // Handle textarea change without auto-resize
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>, questionId: string) => {
    const value = e.target.value
    handleAnswerChange(questionId, value)
  }

  const handleSelectUser = (questionId: string, user: User) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: user.name,
    }))
    setSelectedUsers((prev) => ({
      ...prev,
      [questionId]: user,
    }))

    // Automatically move to next question after selection
    setTimeout(() => {
      const questionIndex = questions.findIndex((q) => q.id === questionId)
      if (
        questionIndex + 1 === currentVisibleQuestions &&
        currentVisibleQuestions < questions.length
      ) {
        setCurrentVisibleQuestions((prev) => prev + 1)
      }
    }, 150)
  }

  const handleClearUser = (questionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: '' }))
    setSelectedUsers((prev) => {
      const newUsers = { ...prev }
      delete newUsers[questionId]
      return newUsers
    })
  }

  const handleMoveToNext = (questionId: string) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId)
    const question = questions[questionIndex]

    // Check if answer is valid
    const hasAnswer = answers[questionId]?.trim()

    // For search type questions, also check if a user is selected
    const isValid =
      hasAnswer &&
      (question?.type !== SurveyQuestionType.SEARCH || selectedUsers[questionId] !== undefined)

    if (!isValid) return

    if (
      questionIndex + 1 === currentVisibleQuestions &&
      currentVisibleQuestions < questions.length
    ) {
      setCurrentVisibleQuestions((prev) => prev + 1)
    }
  }

  const handleSubmit = () => {
    // Check if all questions are answered
    const allQuestionsAnswered = questions.every((q) => {
      const answerValue = answers[q.id]?.trim()
      if (q.type === SurveyQuestionType.SEARCH) {
        return answerValue && selectedUsers[q.id] !== undefined
      }
      return answerValue
    })

    if (!allQuestionsAnswered) {
      toast.error('Vui lòng trả lời tất cả các câu hỏi')
      return
    }

    // Check if this is SendJD (has personNameQuestionId) and no user is selected
    if (personNameQuestionId && Object.keys(selectedUsers).length === 0) {
      toast.error('Vui lòng chọn người nhận')
      return
    }

    const formattedAnswers: Answer[] = []
    let targetUserId: string | undefined

    questions.forEach((question) => {
      if (answers[question.id]) {
        const answerData: Answer = {
          questionId: question.id,
          question: question.text,
          answer: answers[question.id],
        }

        // Extract targetUserId for search type questions
        if (question.type === SurveyQuestionType.SEARCH && selectedUsers[question.id]) {
          targetUserId = selectedUsers[question.id].id
        }

        formattedAnswers.push(answerData)
      }
    })

    // Submit answers and get answerId with preview
    onSubmit(
      { surveyId, answers: formattedAnswers, targetUserId },
      (receivedAnswerId: string, receivedPreviewJD: PreviewJD) => {
        // Store answerId and preview, then show dialog
        setAnswerId(receivedAnswerId)
        setPreviewJD(receivedPreviewJD)
        setShowReviewDialog(true)
      }
    )
  }

  const handleConfirmSubmit = (isAnonymous: boolean) => {
    if (!answerId) return

    // Call confirm API
    confirmJD(
      { answerId, isAnonymous },
      {
        onSuccess: (data) => {
          setShowReviewDialog(false)
          // Navigate based on whether it's for others (SendJD) or self (CreateJD)
          if (personNameQuestionId) {
            // SendJD - navigate to the specific JD detail page
            navigate(`/jd/${data.id}`)
          } else {
            // CreateJD - navigate to my JD page
            navigate('/my-jd')
          }
        },
      }
    )
  }

  const visibleQuestions = questions.slice(0, currentVisibleQuestions)

  // Show submit button when last question is visible
  const isLastQuestionVisible = currentVisibleQuestions === questions.length

  return (
    <GameLayout>
      <div className="w-full max-w-[1300px] mx-auto px-4 mb-20">
        {/* Title Section */}
        <div className="mb-12">
          <h1 className="xl:text-[30px] mb-6 text-[24px] font-bold leading-tight text-black">
            {renderTitle()}
          </h1>
        </div>

        {/* Questions and Answers */}
        <div className="space-y-8">
          {visibleQuestions.map((question, index) => (
            <div key={question.id} className="animate-in fade-in slide-in-from-bottom duration-500">
              {/* Question Bubble - Left side */}
              <div className="mb-6">
                <div className="relative inline-block max-w-[80%]">
                  <div className="bg-primary rounded-[32px] px-8 py-5 text-white shadow-lg">
                    <p className="md:text-xl text-lg font-normal leading-relaxed">
                      {index + 1}. {replacePlaceholders(question.text)}
                    </p>
                  </div>

                  {/* Tail pointing down-left */}
                  <svg
                    className="left-2 -bottom-2 absolute"
                    width="40"
                    height="30"
                    viewBox="0 0 40 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 0 Q20 10, 25 20 Q20 25, 10 30 Q12 15, 15 0"
                      fill="currentColor"
                      className="text-primary"
                    />
                  </svg>
                </div>
              </div>

              {/* Answer Input - Right side */}
              <div className="flex justify-end mb-8">
                <div className="relative w-full max-w-[80%]">
                  {question.type === SurveyQuestionType.SEARCH ? (
                    /* User Search Input for Search Questions */
                    <UserSearchInput
                      selectedUser={selectedUsers[question.id] || null}
                      onSelectUser={(user) => handleSelectUser(question.id, user)}
                      onClearUser={() => handleClearUser(question.id)}
                      disabled={false}
                      placeholder="Tìm kiếm..."
                    />
                  ) : (
                    /* Regular Textarea Field */
                    <>
                      <textarea
                        ref={(el) => {
                          inputRefs.current[question.id] = el as HTMLInputElement | null
                        }}
                        value={answers[question.id] || ''}
                        onChange={(e) => handleTextareaChange(e, question.id)}
                        placeholder="Nhập câu trả lời"
                        rows={1}
                        className="w-full rounded-[16px] px-6 py-4 pr-16 bg-white border-2 border-gray-200 text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-md transition-all resize-none overflow-y-auto"
                        style={{ height: '80px' }}
                      />

                      {/* Send button */}
                      <button
                        onClick={() => handleMoveToNext(question.id)}
                        className="hover:bg-primary/90 active:scale-95 top-1/2 right-3 bg-primary absolute p-3 transition-all duration-200 -translate-y-1/2 rounded-full shadow-md"
                        title="Gửi câu trả lời (Enter)"
                      >
                        <Send className="w-5 h-5 text-white" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Submit Button - Show when last question appears */}
        {isLastQuestionVisible && (
          <div className="animate-in fade-in slide-in-from-bottom flex justify-center mt-12 duration-500">
            <Button onClick={handleSubmit} className="btn-primary">
              Xem kết quả
            </Button>
          </div>
        )}
      </div>

      {/* Review JD Dialog */}
      <ReviewJDDialog
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        onConfirm={handleConfirmSubmit}
        previewJD={previewJD}
        personName={personName || undefined}
        userRole={userRole}
        isLoading={false}
        isConfirming={isConfirming}
        isForOthers={!!personNameQuestionId}
      />
    </GameLayout>
  )
}
